import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import Modal from './Modal';
import { projectTransitionSchema } from '../../lib/project-transition-schemas';
import MediaCaptureForm from './project-transitions/MediaCaptureForm';
import BuildForm from './project-transitions/BuildForm';
import PreviewReviewForm from './project-transitions/PreviewReviewForm';
import ApprovedForm from './project-transitions/ApprovedForm';
import DomainCutoverForm from './project-transitions/DomainCutoverForm';
import LiveForm from './project-transitions/LiveForm';
import MaintainedForm from './project-transitions/MaintainedForm';

const MIN_FORCE_REASON_LEN = 10;

// Project-stage transition dispatcher (item 2.2). Mirrors
// StageTransitionDialog but for project status. The 8 active + 2 terminal
// states map to 7 forward forms + 1 ConfirmDialog flow for canceled/on_hold.
//
// API: POST /api/admin/projects/:id/status with { target, structured_payload, ... }.

const PROJECT_STATUSES = [
  'intake', 'media_capture', 'build', 'preview_review',
  'approved', 'domain_cutover', 'live', 'maintained',
  'canceled', 'on_hold',
];

const PROJECT_TRANSITIONS = {
  intake:         ['media_capture', 'build', 'on_hold', 'canceled'],
  media_capture:  ['build', 'on_hold', 'canceled'],
  build:          ['preview_review', 'media_capture', 'on_hold', 'canceled'],
  preview_review: ['approved', 'build', 'on_hold', 'canceled'],
  approved:       ['domain_cutover', 'preview_review', 'on_hold', 'canceled'],
  domain_cutover: ['live', 'approved', 'on_hold', 'canceled'],
  live:           ['maintained', 'canceled'],
  maintained:     ['canceled'],
  canceled:       [],
  on_hold:        PROJECT_STATUSES.filter((s) => s !== 'on_hold'),
};

const FORM_BY_STATUS = {
  media_capture: MediaCaptureForm,
  build: BuildForm,
  preview_review: PreviewReviewForm,
  approved: ApprovedForm,
  domain_cutover: DomainCutoverForm,
  live: LiveForm,
  maintained: MaintainedForm,
};

function statusLabel(s) {
  return s.replace(/_/g, ' ');
}

export default function ProjectStageTransitionDialog({
  open,
  project,
  onClose,
  onConfirm,
  forceMode = false,
  defaultTarget = '',
}) {
  const currentStatus = project?.status || null;
  const allowed = useMemo(() => PROJECT_TRANSITIONS[currentStatus] || [], [currentStatus]);

  const [target, setTarget] = useState('');
  const [values, setValues] = useState({});
  const [errors, setErrors] = useState(null);
  const [terminalReason, setTerminalReason] = useState('');
  const [overrideReason, setOverrideReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState('');

  useEffect(() => {
    if (!open) return;
    setTarget(defaultTarget || '');
    setValues({});
    setErrors(null);
    setTerminalReason('');
    setOverrideReason('');
    setServerError('');
  }, [open, defaultTarget]);

  useEffect(() => { setValues({}); setErrors(null); setTerminalReason(''); setServerError(''); }, [target]);

  const isTerminal = target === 'canceled' || target === 'on_hold';
  const FormComponent = FORM_BY_STATUS[target];
  const schema = projectTransitionSchema(target);
  const hasFields = !!FormComponent;

  // Client-side required-field check before hitting the server.
  const validate = () => {
    if (isTerminal) return null;
    const errs = {};
    for (const field of schema.fields) {
      const v = values?.[field.key];
      if (!field.required) continue;
      const blank = v == null || v === '' || (typeof v === 'string' && v.trim() === '');
      const blankArr = Array.isArray(v) && v.length === 0;
      if (blank || blankArr || (field.type === 'checkbox' && !v)) {
        errs[field.key] = `${field.label} is required`;
      }
    }
    // media_capture: the shoot window is optional in the schema, but the form
    // must still capture either a start time or an explicit "TBD" so the
    // Upcoming shoots widget can group it. Otherwise CJ has no signal at all.
    if (target === 'media_capture') {
      const hasStart = !!(values?.shoot_start_at && String(values.shoot_start_at).trim());
      const isTbd = !!values?.shoot_date_tbd;
      if (!hasStart && !isTbd) {
        errs.shoot_start_at = 'Pick a shoot date & time or check TBD';
      }
    }
    return Object.keys(errs).length ? errs : null;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    setServerError('');
    if (!target || target === currentStatus) return;
    if (forceMode && overrideReason.trim().length < MIN_FORCE_REASON_LEN) {
      setServerError(`Override reason must be at least ${MIN_FORCE_REASON_LEN} characters.`);
      return;
    }
    const v = validate();
    if (v) { setErrors(v); return; }
    setSubmitting(true);
    try {
      const payload = {
        target,
        structured_payload: isTerminal ? {} : values,
        summary: isTerminal ? terminalReason : undefined,
      };
      if (forceMode) {
        payload.force_status = true;
        payload.override_reason = overrideReason.trim();
      }
      await onConfirm(payload);
      setTarget('');
      setValues({});
      onClose?.();
    } catch (err) {
      setServerError(err?.data?.error || err?.message || 'Status change failed');
    } finally {
      setSubmitting(false);
    }
  };

  const titleVerb = forceMode ? 'Override project status' : 'Change project status';
  const submitLabel = forceMode ? 'Override' : 'Change status';
  const overrideOk = !forceMode || overrideReason.trim().length >= MIN_FORCE_REASON_LEN;

  return (
    <Modal
      open={open}
      onClose={submitting ? () => {} : onClose}
      title={`${titleVerb}${currentStatus ? ` from ${statusLabel(currentStatus)}` : ''}`}
      maxWidth="max-w-lg"
      footer={
        <div className="flex items-center justify-end gap-3 w-full">
          <button type="button" onClick={onClose} disabled={submitting}
            className="px-4 py-2.5 rounded-full border border-white/[0.1] text-sm text-gray-300 hover:text-white">
            Cancel
          </button>
          <button type="submit" form="project-status-form"
            disabled={!target || target === currentStatus || submitting || (isTerminal && !terminalReason.trim()) || !overrideOk}
            className={`px-5 py-2.5 rounded-full text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed ${
              forceMode
                ? 'bg-red-500/90 text-white hover:bg-red-500'
                : 'bg-[#B0FC38] text-gray-900 hover:bg-[#9BE22E] shadow-lg shadow-[#B0FC38]/20'
            }`}>
            {submitting ? (forceMode ? 'Overriding…' : 'Updating…') : submitLabel}
          </button>
        </div>
      }
    >
      <form id="project-status-form" onSubmit={handleSubmit} className="space-y-4">
        {forceMode && (
          <div className="flex gap-3 px-3 py-2 rounded-lg bg-amber-500/[0.08] border border-amber-500/20 text-[12px] text-amber-300/90">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <div>This override is logged in the project's status history with your name and reason.</div>
          </div>
        )}

        <label className="block">
          <span className="block text-[11px] uppercase tracking-wider text-gray-400/80 mb-1.5">Target status</span>
          <select
            data-testid="project-status-target"
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            className="w-full px-3 py-2 rounded-lg bg-white/[0.04] border border-white/[0.08] text-sm text-white"
          >
            <option value="">Select a status…</option>
            {PROJECT_STATUSES.filter((s) => s !== currentStatus).map((s) => {
              const isAllowed = allowed.includes(s);
              const disabled = !forceMode && !isAllowed;
              return (
                <option key={s} value={s} disabled={disabled}>
                  {statusLabel(s)}{!forceMode && !isAllowed ? ' (use force-status)' : ''}
                </option>
              );
            })}
          </select>
        </label>

        {hasFields && (
          <div data-testid={`project-transition-form-${target}`}>
            <FormComponent values={values} onChange={setValues} errors={errors} accountId={project?.account_id} />
          </div>
        )}

        {isTerminal && (
          <label className="block">
            <span className="block text-[11px] uppercase tracking-wider text-gray-400/80 mb-1.5">
              Reason <span className="text-red-400/80">*</span>
            </span>
            <textarea
              data-testid="project-terminal-reason"
              rows={3}
              value={terminalReason}
              onChange={(e) => setTerminalReason(e.target.value)}
              placeholder="Why is this project being canceled / put on hold?"
              className="w-full px-3 py-2 rounded-lg bg-white/[0.04] border border-white/[0.08] text-sm text-white"
            />
          </label>
        )}

        {forceMode && (
          <label className="block">
            <span className="block text-[11px] uppercase tracking-wider text-gray-400/80 mb-1.5">
              Override reason (min {MIN_FORCE_REASON_LEN} chars) <span className="text-red-400/80">*</span>
            </span>
            <textarea
              data-testid="project-override-reason"
              rows={3}
              value={overrideReason}
              onChange={(e) => setOverrideReason(e.target.value)}
              placeholder="Why does the canonical path need to be skipped?"
              className="w-full px-3 py-2 rounded-lg bg-white/[0.04] border border-white/[0.08] text-sm text-white"
            />
          </label>
        )}

        {serverError && (
          <div className="px-3 py-2 rounded-lg bg-red-500/[0.08] border border-red-500/20 text-[13px] text-red-300/90">
            {serverError}
          </div>
        )}
      </form>
    </Modal>
  );
}
